import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { Lock, ArrowLeft, Loader2, CheckCircle, Rocket, AlertCircle } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(null);

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      return;
    }

    // Verify token validity
    const verifyToken = async () => {
      try {
        await axios.post(`${API}/auth/verify-reset-token`, { token });
        setIsValidToken(true);
      } catch (error) {
        setIsValidToken(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setIsSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  // Invalid or missing token
  if (isValidToken === false) {
    return (
      <div className="page-container flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md glass-card p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            Invalid or Expired Link
          </h2>
          <p className="text-zinc-400 text-sm mb-6">
            This password reset link is invalid or has expired. 
            Please request a new one.
          </p>
          <Button
            className="btn-primary"
            onClick={() => navigate("/forgot-password")}
          >
            Request New Link
          </Button>
        </div>
      </div>
    );
  }

  // Loading token verification
  if (isValidToken === null) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="w-16 h-16 rounded-2xl overflow-hidden mx-auto mb-4">
          <img src="/logo192.png" alt="Explorio" className="w-16 h-16" />
        </div>
        <h1 className="text-3xl font-bold font-heading bg-gradient-to-r from-sky-400 to-cyan-300 bg-clip-text text-transparent">
          Explorio
        </h1>
      </div>

      {/* Card */}
      <div className="w-full max-w-md glass-card p-6">
        {!isSuccess ? (
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white mb-2">
                Reset Your Password
              </h2>
              <p className="text-zinc-400 text-sm">
                Enter your new password below.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    className="pl-10 glass-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-password-new"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-300">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    className="pl-10 glass-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-password-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={isLoading}
                data-testid="reset-password-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Password Reset Complete
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Your password has been successfully reset. 
              You can now log in with your new password.
            </p>
            <Button
              className="btn-primary"
              onClick={() => navigate("/")}
            >
              Go to Login
            </Button>
          </div>
        )}

        {/* Back to Login */}
        {!isSuccess && (
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/")}
              className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
